import React, { useEffect, useState } from 'react';
import Drawer from './Drawer';
import OrderKDSModalDetails from './OrderKDSModalDetails';
import ProductsKDSTab from './ProductsKDSTab';
import { CheckCircle, Clock, XCircle, Mail, Search } from 'react-feather';
import useWebSocket from '@/hooks/useWebSocket';
import events from '@/constants/websocketEvents';
import axios from 'axios';
import sound from '@/assets/media/sounds/notification.mp3';
import { useOrdersContext } from '../contexts/OrderContext';
import { useRestaurantsContext } from '../contexts/RestaurantContext';
import { toast } from 'react-toastify';

const statusColors = {
    paid: 'bg-emerald-200 border-emerald-600',
    pending: 'bg-yellow-100 border-yellow-500',
    declined: 'bg-red-100 border-red-500',
    validated: 'bg-green-100 border-green-500',
    processing: 'bg-blue-100 border-blue-500',
    completed: 'bg-purple-100 border-purple-500',
};

const statuses = {
    pending: {
        label: 'En attente de validation',
    },
    validated: {
        label: 'Virement',
    },
    canceled: {
        label: 'Annulée',
    },
    pending_payment: {
        label: 'En attente de paiement',
    },
    paid: {
        label: 'Payée',
    },
    declined: {
        label: 'Refusée',
    },
    processing: {
        label: 'En cours de traitement',
    },
    shipping: {
        label: 'En cours de livraison',
    },
    completed: {
        label: 'Terminée',
    },
};

const NOTIFICATION_SOUND = new Audio(sound);
NOTIFICATION_SOUND.volume = 0.3;

const KDS = ({ orders, onOrderUpdate, selectedRestaurantFromParent }) => {
    const [showDrawer, setShowDrawer] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [pendingOrders, setPendingOrders] = useState([]);
    const [previousValidatedCount, setPreviousValidatedCount] = useState(0);
    const [activeTab, setActiveTab] = useState('orders');
    const { selectedRestaurant: contextRestaurant } = useRestaurantsContext();
    const { getRestaurantId } = useOrdersContext();
    const [duplicateGroups, setDuplicateGroups] = useState({});
    const [selectedDuplicateGroup, setSelectedDuplicateGroup] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Utiliser le restaurant du parent en priorité, sinon celui du contexte
    const selectedRestaurant = selectedRestaurantFromParent || contextRestaurant;
    
    // Log pour déboguer
    useEffect(() => {
        console.log('KDS - Restaurant du contexte:', contextRestaurant);
        console.log('KDS - Restaurant du parent:', selectedRestaurantFromParent);
        console.log('KDS - Restaurant utilisé:', selectedRestaurant);
        console.log('KDS - Mode Haguim:', selectedRestaurant?.is_haguim_mode);
    }, [contextRestaurant, selectedRestaurantFromParent, selectedRestaurant]);

    const fetchPendingOrders = async () => {
        try {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/orders/all`, {
                params: {
                    status: 'pending_payment',
                    include_details: true,
                    restaurantId: selectedRestaurant.id
                },
            });

            if (response.status === 200) {
                console.log('Pending orders:', response.data);
                
                const orders = response.data.orders.map(order => {
                    if (!order.categories) order.categories = [];
                    if (!order.selected_menus) order.selected_menus = [];
                    return order;
                });
                
                setPendingOrders(orders);
                
                // Identifier les doublons basés sur l'email du client
                const emailGroups = {};
                orders.forEach(order => {
                    const email = order.billing?.customer?.email;
                    if (email) {
                        if (!emailGroups[email]) {
                            emailGroups[email] = [];
                        }
                        emailGroups[email].push(order);
                    }
                });
                
                // Ne garder que les groupes avec plus d'une commande (doublons)
                const duplicates = Object.fromEntries(
                    Object.entries(emailGroups).filter(([_, orders]) => orders.length > 1)
                );
                
                setDuplicateGroups(duplicates);
            }
        } catch (error) {
            console.error('Error fetching pending orders:', error);
        }
    };

    const playNotificationSound = async () => {
        try {
            await new Promise(resolve => setTimeout(resolve, 100));
            NOTIFICATION_SOUND.currentTime = 0;
            await NOTIFICATION_SOUND.play();
            console.log('Son de notification joué avec succès');
        } catch (error) {
            console.error('Erreur lors de la lecture du son:', error);
        }
    };

    useEffect(() => {
        console.log("Onglet actif:", activeTab);
    }, [activeTab]);

    useEffect(() => {
        if (orders) {
            fetchPendingOrders();
            
            const currentValidatedOrders = orders.filter(order => 
                ['validated', 'processing', 'completed', 'paid'].includes(order.status)
            ).length;

            console.log('Commandes validées:', currentValidatedOrders, 'Précédentes:', previousValidatedCount);
            console.log('Mode Haguim activé:', selectedRestaurant?.is_haguim_mode);

            if (currentValidatedOrders > previousValidatedCount) {
                console.log('Nouvelle commande validée détectée!');
                playNotificationSound();
            }

            setPreviousValidatedCount(currentValidatedOrders);
        }
    }, [orders, previousValidatedCount]);

    const handleOpenDrawer = (order) => {
        setSelectedOrder(order);
        setShowDrawer(true);
    };

    const handleCloseDrawer = () => {
        setShowDrawer(false);
        setSelectedOrder(null);
    };

    const handleOrderStatusChange = (updatedOrder) => {
        if (onOrderUpdate) {
            onOrderUpdate(updatedOrder);
        }
        handleCloseDrawer();
    };

    // Calculer les dates de début et de fin pour le filtre de samedi à samedi
    const getSaturdayRange = () => {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const lastSaturday = new Date(today);
        lastSaturday.setDate(today.getDate() - dayOfWeek - 1);
        lastSaturday.setHours(0, 0, 0, 0);

        const nextSaturday = new Date(lastSaturday);
        nextSaturday.setDate(lastSaturday.getDate() + 7);
        nextSaturday.setHours(23, 59, 59, 999);

        return { lastSaturday, nextSaturday };
    };

    const { lastSaturday, nextSaturday } = getSaturdayRange();

    // Filtrer les commandes pour n'inclure que celles de samedi à samedi
    const filteredOrders = orders.filter(order => {
        // Log pour déboguer
        console.log('Filtrage de commande:', order.reference, 'Date:', order.created_at, 'Mode Haguim:', selectedRestaurant?.is_haguim_mode);
        
        // Utiliser la valeur réelle du mode Haguim
        if (selectedRestaurant?.is_haguim_mode) {
            console.log('Mode Haguim activé, pas de filtrage de date');
            return true;
        }
        
        const orderDate = new Date(order.created_at);
        return orderDate >= lastSaturday && orderDate < nextSaturday;
    });

    // Filtrer par terme de recherche
    const filterBySearchTerm = (ordersList) => {
        if (!searchTerm.trim()) return ordersList;
        
        return ordersList.filter(order => {
            const customerName = `${order.delivery.customer.firstname} ${order.delivery.customer.lastname}`.toLowerCase();
            const reference = order.reference.toString().toLowerCase();
            const customerEmail = order.billing?.customer?.email?.toLowerCase() || '';
            
            return customerName.includes(searchTerm.toLowerCase()) || 
                   reference.includes(searchTerm.toLowerCase()) ||
                   customerEmail.includes(searchTerm.toLowerCase());
        });
    };

    // Filtrage des commandes par statut
    const declinedOrders = filterBySearchTerm(filteredOrders.filter(order => 
        order.status === 'declined'
    ));
    
    const validatedOrders = filterBySearchTerm(filteredOrders.filter(order => 
        ['validated', 'processing', 'completed', 'paid'].includes(order.status)
    ));

    const filteredPendingOrders = filterBySearchTerm(pendingOrders);

    const getStatusIcon = (status) => {
        switch (status) {
            case 'validated':
                return <CheckCircle className="text-green-500 mr-2" />;
            case 'paid':
                return <CheckCircle className="text-emerald-600 mr-2" />;
            case 'processing':
                return <Clock className="text-blue-500 mr-2" />;
            case 'completed':
                return <CheckCircle className="text-purple-500 mr-2" />;
            case 'declined':
                return <XCircle className="text-red-500 mr-2" />;
            case 'pending':
                return <Clock className="text-yellow-500 mr-2" />;
            default:
                return <Clock className="text-gray-500 mr-2" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'validated':
                return statusColors.validated;
            case 'paid':
                return statusColors.paid;
            case 'processing':
                return statusColors.processing;
            case 'completed':
                return statusColors.completed;
            case 'declined':
                return statusColors.declined;
            case 'pending':
                return statusColors.pending;
            default:
                return 'bg-gray-100 border-gray-500';
        }
    };

    const deleteOrder = async (orderId, restaurantId) => {
        try {
            const response = await axios.delete(
                `${import.meta.env.VITE_API_URL}/api/back-office/${restaurantId}/orders/${orderId}/delete`
            );

            if (response.status === 200) {
                toast.success('Commande supprimée avec succès');
                // Rafraîchir les données
                fetchPendingOrders();
                if (onOrderUpdate) {
                    onOrderUpdate(null);
                }
            }
        } catch (error) {
            console.error('Erreur lors de la suppression de la commande:', error);
            toast.error('Erreur lors de la suppression de la commande');
        }
    };

    useWebSocket(events.TEMPORARY_ORDER_CREATED, fetchPendingOrders);
    useWebSocket(events.ORDER_STATUS_UPDATED, () => {
        console.log('Statut de commande mis à jour');
        playNotificationSound();
    });

    // Fonction pour suggérer la commande à supprimer
    const getSuggestedOrderToDelete = (orders) => {
        if (!orders || orders.length <= 1) return null;
        
        // Critères de sélection:
        // 1. Préférer la commande la plus ancienne
        // 2. En cas d'égalité (créées dans la même minute), prendre celle avec le moins de produits
        
        return orders.reduce((suggested, current) => {
            if (!suggested) return current;
            
            const suggestedDate = new Date(suggested.created_at);
            const currentDate = new Date(current.created_at);
            
            // Si la différence est de plus de 5 minutes, choisir la plus ancienne
            if (Math.abs(suggestedDate - currentDate) > 5 * 60 * 1000) {
                return suggestedDate < currentDate ? suggested : current;
            }
            
            // Si créées à peu près en même temps, comparer le nombre de produits
            const suggestedProductCount = countProducts(suggested);
            const currentProductCount = countProducts(current);
            
            return suggestedProductCount <= currentProductCount ? suggested : current;
        }, null);
    };

    // Compter le nombre total de produits dans une commande
    const countProducts = (order) => {
        let count = 0;
        
        // Compter les produits dans les menus
        if (order.selected_menus) {
            order.selected_menus.forEach(menu => {
                count += menu.quantity || 0;
            });
        }
        
        // Compter les produits individuels par catégorie
        if (order.categories) {
            order.categories.forEach(category => {
                if (category.products) {
                    category.products.forEach(product => {
                        count += product.quantity || 0;
                    });
                }
                if (category.extra_products) {
                    category.extra_products.forEach(product => {
                        count += product.quantity || 0;
                    });
                }
            });
        }
        
        return count;
    };

    // Rendre le composant du comparateur de commandes
    const renderDuplicateComparer = () => {
        const duplicateEmails = Object.keys(duplicateGroups);
        
        if (duplicateEmails.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center h-full">
                    <p className="text-lg text-gray-600">Aucun doublon trouvé parmi les commandes en attente</p>
                </div>
            );
        }
        
        // Trouver les commandes validées avec les mêmes emails
        const findValidatedOrdersByEmail = (email) => {
            return orders.filter(order => 
                ['validated', 'processing', 'completed', 'paid'].includes(order.status) && 
                order.billing?.customer?.email === email
            );
        };
        
        return (
            <div className="flex w-full h-full overflow-hidden md:flex-row flex-col">
                <div className="w-full md:w-1/3 p-4 overflow-y-auto flex flex-col items-center border-r border-gray-200">
                    <h2 className="text-lg font-bold mb-4 text-blue-600 text-center">
                        Emails avec commandes en double ({duplicateEmails.length})
                    </h2>
                    <div className="w-full max-w-md">
                        {duplicateEmails.map((email) => {
                            const pendingCount = duplicateGroups[email].length;
                            const validatedCount = findValidatedOrdersByEmail(email).length;
                            
                            return (
                                <div 
                                    key={email} 
                                    className={`border-l-4 ${selectedDuplicateGroup === email ? 'bg-blue-100 border-blue-500' : 'bg-gray-100 border-gray-500'} rounded-lg shadow-md p-4 mb-4 cursor-pointer transition-transform transform hover:scale-105`}
                                    onClick={() => setSelectedDuplicateGroup(email)}
                                >
                                    <div className="flex items-center">
                                        <Mail className="text-blue-500 mr-2" />
                                        <div>
                                            <h3 className="text-lg font-bold">{email}</h3>
                                            <p className="text-sm text-gray-500">
                                                {pendingCount} commandes en attente
                                                {validatedCount > 0 && 
                                                    ` • ${validatedCount} commande(s) validée(s)`}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                
                <div className="w-full md:w-2/3 p-4 overflow-y-auto">
                    {selectedDuplicateGroup && (
                        <div>
                            <h2 className="text-lg font-bold mb-4 text-center">
                                Commandes pour {selectedDuplicateGroup}
                            </h2>
                            
                            {/* Commandes en attente */}
                            <div className="mb-6">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-md font-semibold text-yellow-600 border-b pb-1">
                                        Commandes en attente ({duplicateGroups[selectedDuplicateGroup].length})
                                    </h3>
                                    
                                    {duplicateGroups[selectedDuplicateGroup].length > 1 && (
                                        <div className="flex flex-col">
                                            <div className="bg-red-100 text-red-800 px-3 py-1 rounded text-sm">
                                                Doublons détectés !
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="grid md:grid-cols-2 gap-4">
                                    {duplicateGroups[selectedDuplicateGroup].map((order) => {
                                        const suggestedToDelete = getSuggestedOrderToDelete(duplicateGroups[selectedDuplicateGroup]);
                                        const isSuggested = suggestedToDelete && order.reference === suggestedToDelete.reference;
                                        
                                        return (
                                            <div 
                                                key={order.reference} 
                                                className={`border rounded-lg shadow-md p-4 ${isSuggested ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-200'}`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <h3 className="font-bold">#{order.reference}</h3>
                                                    <span className="text-sm text-gray-500">
                                                        {new Date(order.created_at).toLocaleString()}
                                                    </span>
                                                </div>
                                                
                                                {isSuggested && (
                                                    <div className="mb-2 bg-red-100 text-red-800 px-2 py-1 rounded text-sm inline-block">
                                                        Suggestion: À supprimer
                                                    </div>
                                                )}
                                                
                                                <div className="mb-2">
                                                    <p className="text-gray-700">{order.delivery.customer.firstname} {order.delivery.customer.lastname}</p>
                                                    <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
                                                    {order.delivery.customer.phone && (
                                                        <p className="text-sm text-gray-700 mt-2">
                                                            <span className="font-semibold">Tél:</span> {order.delivery.customer.phone}
                                                        </p>
                                                    )}
                                                </div>
                                                
                                                <div className="mt-2 pt-2 border-t border-gray-200">
                                                    <p className="font-semibold">Total: {order.total_price}€</p>
                                                </div>
                                                
                                                <div className="mt-4 flex justify-between">
                                                    <button 
                                                        className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors"
                                                        onClick={() => handleOpenDrawer(order)}
                                                    >
                                                        Détails
                                                    </button>
                                                    <button 
                                                        className={`${isSuggested ? 'bg-red-600' : 'bg-red-500'} text-white px-3 py-1 rounded hover:bg-red-700 transition-colors ${isSuggested ? 'animate-pulse' : ''}`}
                                                        onClick={() => deleteOrder(order.reference, order.restaurant_id)}
                                                    >
                                                        Supprimer
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            
                            {/* Commandes validées */}
                            {findValidatedOrdersByEmail(selectedDuplicateGroup).length > 0 && (
                                <div>
                                    <h3 className="text-md font-semibold mb-2 text-green-600 border-b pb-1">
                                        Commandes validées ({findValidatedOrdersByEmail(selectedDuplicateGroup).length})
                                    </h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {findValidatedOrdersByEmail(selectedDuplicateGroup).map((order) => (
                                            <div 
                                                key={order.reference} 
                                                className="border border-green-200 rounded-lg shadow-md p-4 bg-green-50"
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center">
                                                        {getStatusIcon(order.status)}
                                                        <h3 className="font-bold">#{order.reference}</h3>
                                                    </div>
                                                    <span className="text-sm text-gray-500">
                                                        {new Date(order.created_at).toLocaleString()}
                                                    </span>
                                                </div>
                                                
                                                <div className="mb-2">
                                                    <p className="text-gray-700">{order.delivery.customer.firstname} {order.delivery.customer.lastname}</p>
                                                    <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
                                                    {order.delivery.customer.phone && (
                                                        <p className="text-sm text-gray-700 mt-2">
                                                            <span className="font-semibold">Tél:</span> {order.delivery.customer.phone}
                                                        </p>
                                                    )}
                                                </div>
                                                
                                                <div className="mt-2 pt-2 border-t border-gray-200">
                                                    <p className="font-semibold">Total: {order.total_price}€</p>
                                                </div>
                                                
                                                <div className="mt-4">
                                                    <button 
                                                        className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors w-full"
                                                        onClick={() => handleOpenDrawer(order)}
                                                    >
                                                        Détails
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col w-full h-full bg-gray-50">
            {/* Onglets */}
            <div className="flex justify-center items-center p-4">
                <div className="bg-gray-200 p-1 rounded-lg flex gap-1">
                    <button
                        className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                            activeTab === 'orders'
                                ? 'bg-white text-primary shadow-md transform scale-105'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                        onClick={() => setActiveTab('orders')}
                    >
                        Commandes
                    </button>
                    <button
                        className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                            activeTab === 'products'
                                ? 'bg-white text-primary shadow-md transform scale-105'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                        onClick={() => setActiveTab('products')}
                    >
                        Produits
                    </button>
                    <button
                        className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 border-2 ${
                            activeTab === 'duplicates'
                                ? 'bg-white text-blue-600 shadow-md transform scale-105 border-blue-500'
                                : 'text-blue-600 hover:text-blue-800 border-blue-300 hover:border-blue-500'
                        }`}
                        onClick={() => {
                            setActiveTab('duplicates');
                            fetchPendingOrders(); // Rafraîchir les données lors du changement d'onglet
                        }}
                    >
                        Commande en double
                    </button>
                </div>
            </div>

            {/* Champ de recherche */}
            {activeTab === 'orders' && (
                <div className="px-4 pb-4 w-full max-w-md mx-auto">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            className="block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Rechercher par nom ou numéro de commande"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                onClick={() => setSearchTerm('')}
                            >
                                <XCircle className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Contenu des onglets */}
            {activeTab === 'orders' && (
                <div className="flex w-full h-full overflow-hidden md:flex-row flex-col">
                    <div className="w-full md:w-1/3 p-4 overflow-y-auto flex flex-col items-center border-r border-gray-200">
                        <h2 className="text-lg font-bold mb-4 text-yellow-600 text-center">
                            Commandes en Attente ({filteredPendingOrders.length})
                        </h2>
                        <div className="w-full max-w-md">
                            {filteredPendingOrders.length > 0 && filteredPendingOrders.map((order) => (
                                <div key={order.reference} 
                                     className={`border-l-4 ${getStatusColor(order.status)} rounded-lg shadow-md p-4 mb-4 cursor-pointer transition-transform transform hover:scale-105`}
                                     onClick={() => handleOpenDrawer(order)}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            {getStatusIcon(order.status)}
                                            <h3 className="text-lg font-bold">Commande #{order.reference}</h3>
                                        </div>
                                        <span className="text-sm text-gray-500">{statuses[order.status]?.label}</span>
                                    </div>
                                    <p className="text-gray-700">{order.delivery.customer.firstname} {order.delivery.customer.lastname}</p>
                                    <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
                                    {order.delivery.customer.phone && (
                                        <p className="text-sm text-gray-700 mt-2">
                                            <span className="font-semibold">Tél:</span> {order.delivery.customer.phone}
                                        </p>
                                    )}
                                </div>
                            ))}
                            {filteredPendingOrders.length === 0 && searchTerm && (
                                <div className="text-center py-4 text-gray-500">
                                    Aucune commande en attente ne correspond à votre recherche
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="w-full md:w-1/3 p-4 overflow-y-auto flex flex-col items-center border-r border-gray-200">
                        <h2 className="text-lg font-bold mb-4 text-red-600 text-center">
                            Commandes Refusées ({declinedOrders.length})
                        </h2>
                        <div className="w-full max-w-md">
                            {declinedOrders.map((order) => (
                                <div key={order.reference} 
                                     className={`border-l-4 ${getStatusColor(order.status)} rounded-lg shadow-md p-4 mb-4 cursor-pointer transition-transform transform hover:scale-105`}
                                     onClick={() => handleOpenDrawer(order)}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            {getStatusIcon(order.status)}
                                            <h3 className="text-lg font-bold">Commande #{order.reference}</h3>
                                        </div>
                                        <span className="text-sm text-gray-500">{statuses[order.status]?.label}</span>
                                    </div>
                                    <p className="text-gray-700">{order.delivery.customer.firstname} {order.delivery.customer.lastname}</p>
                                    <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
                                    {order.delivery.customer.phone && (
                                        <p className="text-sm text-gray-700 mt-2">
                                            <span className="font-semibold">Tél:</span> {order.delivery.customer.phone}
                                        </p>
                                    )}
                                </div>
                            ))}
                            {declinedOrders.length === 0 && searchTerm && (
                                <div className="text-center py-4 text-gray-500">
                                    Aucune commande refusée ne correspond à votre recherche
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="w-full md:w-1/3 p-4 overflow-y-auto flex flex-col items-center">
                        <h2 className="text-lg font-bold mb-4 text-green-600 text-center">
                            Commandes Validées ({validatedOrders.length})
                        </h2>
                        <div className="w-full max-w-md">
                            {validatedOrders.map((order) => (
                                <div key={order.reference} 
                                     className={`border-l-4 ${getStatusColor(order.status)} rounded-lg shadow-md p-4 mb-4 cursor-pointer transition-transform transform hover:scale-105`}
                                     onClick={() => handleOpenDrawer(order)}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            {getStatusIcon(order.status)}
                                            <h3 className="text-lg font-bold">Commande #{order.reference}</h3>
                                        </div>
                                        <span className="text-sm text-gray-500">{statuses[order.status]?.label}</span>
                                    </div>
                                    <p className="text-gray-700">{order.delivery.customer.firstname} {order.delivery.customer.lastname}</p>
                                    <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
                                    {order.delivery.customer.phone && (
                                        <p className="text-sm text-gray-700 mt-2">
                                            <span className="font-semibold">Tél:</span> {order.delivery.customer.phone}
                                        </p>
                                    )}
                                </div>
                            ))}
                            {validatedOrders.length === 0 && searchTerm && (
                                <div className="text-center py-4 text-gray-500">
                                    Aucune commande validée ne correspond à votre recherche
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {activeTab === 'products' && (
                <ProductsKDSTab restaurantId={selectedRestaurant?.id} />
            )}
            
            {activeTab === 'duplicates' && (
                renderDuplicateComparer()
            )}

            <Drawer isOpen={showDrawer} onClose={handleCloseDrawer}>
                <OrderKDSModalDetails 
                    order={selectedOrder} 
                    onClose={handleCloseDrawer}
                    onStatusChange={handleOrderStatusChange}
                />
            </Drawer>
        </div>
    );
};

export default KDS;